import React, { useState, useEffect } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { useLanguage } from '@/context/LanguageContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Edit, X } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { UPDATE_PRODUCT_ATTRIBUTES, GET_PRODUCT_DETAILS } from '../productQueries'
import { gql } from '@apollo/client'

const GET_ALL_ATTRIBUTES_GROUPED = gql`
  query GET_ALL_ATTRIBUTES_GROUPED {
    findManyProductAttributeGroup {
      id
      product_attribute_group_profiles {
        name
        language
      }
      product_attributes {
        id
        product_attribute_profiles {
          id
          name
          language
        }
      }
    }
  }
`

interface AssignedAttribute {
  id: number
  product_attribute: {
    id: number
    product_attribute_profiles: {
      id: number
      name: string
      description?: string
      language: string
    }[]
    product_attribute_group: {
      id: number
      product_attribute_group_profiles: {
        name: string
        language: string
      }[]
    }
  }
}

interface ProductAttributesCardProps {
  productId: number
  assignedAttributes: AssignedAttribute[]
  language: string
}

export function ProductAttributesCard({
  productId,
  assignedAttributes,
  language,
}: ProductAttributesCardProps) {
  const { language: currentLanguage } = useLanguage()
  const [isEditing, setIsEditing] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  const [loadGroups, { data: groupsData, loading: loadingGroups }] =
    useLazyQuery(GET_ALL_ATTRIBUTES_GROUPED, {
      fetchPolicy: 'cache-and-network',
    })

  const [updateAttributes, { loading: updating }] = useMutation(
    UPDATE_PRODUCT_ATTRIBUTES,
    {
      refetchQueries: [
        { query: GET_PRODUCT_DETAILS, variables: { id: productId } },
      ],
      onCompleted: () => {
        setIsEditing(false)
      },
    }
  )

  const groups = groupsData?.findManyProductAttributeGroup || []

  useEffect(() => {
    setSelectedIds(
      new Set(assignedAttributes.map(a => a.product_attribute.id))
    )
  }, [assignedAttributes])

  const getName = (profiles: { name: string; language: string }[]) => {
    return (
      profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      profiles[0]?.name ||
      'Unknown'
    )
  }

  const handleToggle = (attributeId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(attributeId)) {
        next.delete(attributeId)
      } else {
        next.add(attributeId)
      }
      return next
    })
  }

  const handleSave = async () => {
    try {
      await updateAttributes({
        variables: {
          id: productId,
          product_attributes: Array.from(selectedIds).map(id => ({ id })),
        },
      })
    } catch (error) {
      console.error('Error updating product attributes:', error)
    }
  }

  const handleCancel = () => {
    setSelectedIds(
      new Set(assignedAttributes.map(a => a.product_attribute.id))
    )
    setIsEditing(false)
  }

  const originalIds = new Set(
    assignedAttributes.map(a => a.product_attribute.id)
  )
  const hasChanges =
    selectedIds.size !== originalIds.size ||
    [...selectedIds].some(id => !originalIds.has(id))

  // Group assigned attributes by group for display
  const groupedAssigned = assignedAttributes.reduce(
    (acc, attr) => {
      const groupName = getName(
        attr.product_attribute.product_attribute_group
          .product_attribute_group_profiles
      )
      if (!acc[groupName]) acc[groupName] = []
      acc[groupName].push(attr)
      return acc
    },
    {} as Record<string, AssignedAttribute[]>
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Product Attributes</CardTitle>
        <div className="flex space-x-2">
          {!isEditing && (
            <Button
              onClick={() => {
                setIsEditing(true)
                loadGroups()
              }}
              size="sm"
              variant="ghost"
            >
              <Edit className="h-4 w-4 mr-2" />
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                onClick={handleCancel}
                size="sm"
                variant="ghost"
                disabled={updating}
              >
                <X className="h-4 w-4 text-red-500" />
                Cancel
              </Button>
              {hasChanges && (
                <Button
                  onClick={handleSave}
                  size="sm"
                  variant="default"
                  disabled={updating}
                >
                  Save Changes
                </Button>
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!isEditing ? (
          // Read mode: show assigned attributes grouped
          <div className="space-y-3">
            {Object.keys(groupedAssigned).length > 0 ? (
              Object.entries(groupedAssigned).map(([groupName, attrs]) => (
                <div key={groupName}>
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {groupName}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {attrs.map(attr => (
                      <span
                        key={attr.product_attribute.id}
                        className="text-sm bg-secondary px-2 py-0.5 rounded"
                      >
                        {getName(
                          attr.product_attribute.product_attribute_profiles
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">
                No attributes assigned to this product.
              </p>
            )}
          </div>
        ) : (
          // Edit mode: grouped checkboxes
          <div className="space-y-4">
            {loadingGroups ? (
              <div className="text-sm text-gray-500">Loading attributes...</div>
            ) : groups.length > 0 ? (
              groups.map((group: any) => (
                <div key={group.id}>
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    {getName(group.product_attribute_group_profiles)}
                  </p>
                  <div className="space-y-2">
                    {group.product_attributes.map((attr: any) => (
                      <label
                        key={attr.id}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedIds.has(attr.id)}
                          onCheckedChange={() => handleToggle(attr.id)}
                        />
                        <span className="text-sm">
                          {getName(attr.product_attribute_profiles)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">
                No attribute groups available.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
